import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import Sidebar from '../../components/SideBar/Sidebar';
import * as Body from './Styles';
import SearchIcon from '../../../assets/icons/Search.svg';
import AddIcon from '../../../assets/icons/add-icon.svg';
import * as Functions from '../../../utils/functions/Functions';
import * as defines from '../../../utils/defines/Defines';
import Input from '../../components/Inputs/Input/Input';
import Button from '../../components/Buttons/Button/Button';
import * as Modals from './Modals/Modals';
import Loading from '../../components/Loading/Loading';
import Avatar from '../../../assets/images/avatar.png';
import SendInvite from './Modals/SendInvite';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ButtonWithoutBG from '../../components/Buttons/ButtonWithoutBG/Button';
import HardDelete from './Modals/HardDelete';
import { State } from '../../redux/store';

type UsuarioProps = {
  idEmpresarial: string;
  idUsuario: string;
  fotoUsuario: string | null;
  ddd: string;
  celular: string;
  nomeCompleto: string | null;
  email: string;
  cargo: number;
  encarregadoDados: number;
};

const ManageUsers = () => {
  // Dados da rota
  const { state } = useLocation();
  const { idEmpresa, nomeEmpresa, idFilial, nomeFilial } = state || {};
  
  // Redux state
  const acessLevel = useSelector((state: State) => state.acessLevel.acessLevel);
  const userType = useSelector((state: State) => state.userType.userType);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Estados do componente
  const [modalStates, setModalStates] = useState({
    createUser: false,
    delete: false,
    hardDelete: false,
  });
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UsuarioProps[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UsuarioProps[]>([]);
  const [selectedUser, setSelectedUser] = useState<UsuarioProps | null>(null);
  const [userForm, setUserForm] = useState({
    nomeCompleto: '',
    email: '',
    ddd: '',
    celular: '',
  });

  const inputRef = useRef<HTMLInputElement>(null);

  // Funções auxiliares
  const getCargo = (cargo: number) => {
    switch(cargo){
      case defines.CargoEmpresarial.Administrador:
        return 'Administrador'
      case defines.CargoEmpresarial.Padrão:
        return 'Padrão'
      default:
        return 'Desconhecido'
    }
  };

  const handleError = (error: any) => {
    Functions.ResolveErrors(
      error.response?.status,
      error.response?.data?.code,
      error.response?.data?.message,
      navigate,
      dispatch,
      error.response?.data?.content
    );
    setLoading(false);
  };

  // Buscar usuários
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${defines.apiURL}/api/EmpresarialCRUD?idFilial=${Functions.ReplaceSpecialCharacters(idFilial)}&userTypeRequest=${userType}`,
        { withCredentials: true }
      );
      
      // Filtrar usuários com nome null e ordenar
      const validUsers = response.data.content.filter((user: UsuarioProps) => user.nomeCompleto);
      const sortedUsers = Functions.SortResponse(validUsers, 'nomeCompleto');
      
      setUsers(sortedUsers);
      setFilteredUsers(sortedUsers);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Selecionar usuário
  const selectUser = async (id: string) => {
    if (selectedUser?.idUsuario === id) {
      setSelectedUser(null);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${defines.apiURL}/api/EmpresarialCRUD?id=${Functions.ReplaceSpecialCharacters(id)}&userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(idFilial)}`,
        { withCredentials: true }
      );

      const userData = response.data.content[0];
      setSelectedUser(userData);
      setUserForm({
        nomeCompleto: userData.nomeCompleto || '',
        email: userData.email,
        ddd: userData.ddd,
        celular: userData.celular,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar usuários
  const filterUsers = (name: string) => {
    if (!name) {
      setFilteredUsers(users);
      return;
    }
    
    const filtered = users.filter(user => 
      user.nomeCompleto?.toUpperCase().includes(name.toUpperCase())
    );
    setFilteredUsers(filtered);
  };

  // Atualizar usuário
  const updateUser = async () => {
    if (!selectedUser) return;

    const updatedFields: Partial<UsuarioProps> = {};
    if (userForm.email !== selectedUser.email) updatedFields.email = userForm.email;
    if (userForm.celular !== selectedUser.celular) updatedFields.celular = userForm.celular;
    if (userForm.ddd !== selectedUser.ddd) updatedFields.ddd = userForm.ddd;
    if (userForm.nomeCompleto !== selectedUser.nomeCompleto) updatedFields.nomeCompleto = userForm.nomeCompleto;

    if (Object.keys(updatedFields).length === 0) return;

    setLoading(true);
    try {
      await axios.put(
        `${defines.apiURL}/api/UpdateUser?userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(idFilial)}`,
        {
          ...updatedFields,
          idUsuario: selectedUser.idUsuario,
          idEmpresarial: selectedUser.idEmpresarial,
        },
        { withCredentials: true }
      );

      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        title: '',
        text: 'Dados do usuário alterados com sucesso',
      });
      
      fetchUsers();
      selectUser(selectedUser.idUsuario);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Desvincular usuário
  const deleteUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await axios.delete(
        `${defines.apiURL}/api/EmpresarialCRUD?id=${Functions.ReplaceSpecialCharacters(selectedUser.idEmpresarial)}&userTypeRequest=${userType}&idFilial=${Functions.ReplaceSpecialCharacters(idFilial)}`,
        { withCredentials: true }
      );

      dispatch({
        type: 'ADD_NOTIFICATION',
        status: 1,
        text: 'Usuário desvinculado com sucesso',
        title: '',
      });
      
      setSelectedUser(null);
      setModalStates(prev => ({ ...prev, delete: false }));
      fetchUsers();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Deletar permanentemente
  const hardDeleteUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      await axios.delete(
        `${defines.apiURL}/api/UsuarioCRUD?idFilial=${Functions.ReplaceSpecialCharacters(idFilial)}&userTypeRequest=${userType}&id=${Functions.ReplaceSpecialCharacters(selectedUser.idUsuario)}`,
        { withCredentials: true }
      );

      setSelectedUser(null);
      setModalStates(prev => ({ ...prev, hardDelete: false }));
      fetchUsers();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Efeitos
  useEffect(() => {
    if (idFilial) fetchUsers();
  }, [idFilial]);

  // Renderização
  return (
    <Body.Container>
      <Sidebar type={userType} content={defines.ContentSidebar.MANAGE_COMPANY} />
      
      <Body.Content style={{ flexDirection: 'row' }}>
        {/* Lista de usuários */}
        <Body.HalfContent style={{ width: selectedUser ? 'calc(100% - 120px)' : '100%' }}>
          <Body.Row style={{ width: '100%', height: '140px', justifyContent: 'space-between', alignItems: 'center' }}>
            <Breadcrumb empresa={nomeEmpresa} filial={nomeFilial} disableSublinks />
            
            <Body.Row style={{ maxWidth: '90%', minWidth: '50px', alignItems: 'center' }}>
              <ButtonWithoutBG
                text="Trocar Filial"
                action={() => navigate('/empresas/filiais', { state: { idEmpresa, nome: nomeEmpresa } })}
              />
              
              <Body.FindContent>
                <Body.Find
                  onChange={(e) => filterUsers(e.target.value)}
                  ref={inputRef}
                />
                <Body.Icon
                  src={SearchIcon}
                  style={{ cursor: 'text' }}
                  onClick={() => inputRef.current?.focus()}
                />
              </Body.FindContent>
              
              {acessLevel !== defines.CargoTectrol['Padrão'] && (
                <Body.HeaderButton onClick={() => setModalStates(prev => ({ ...prev, createUser: true }))}>
                  <Body.Icon src={AddIcon} style={{ position: 'relative', top: 0, left: 0 }} />
                </Body.HeaderButton>
              )}
            </Body.Row>
          </Body.Row>
          
          <Body.Scroll style={{ maxHeight: 'calc(100% - 120px)' }}>
            {filteredUsers.length === 0 ? (
              <Body.Text>Nenhum usuário encontrado</Body.Text>
            ) : (
              filteredUsers.map((user) => (
                <Body.SelectUser
                  key={user.idUsuario}
                  onClick={() => selectUser(user.idUsuario)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Body.Photo src={user.fotoUsuario || Avatar} />
                    <Body.Text>{user.nomeCompleto}</Body.Text>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                    <Body.NoteText>{getCargo(user.cargo)}</Body.NoteText>
                    {user.encarregadoDados ? <Body.NoteText>Encarregado de Dados</Body.NoteText> : null}
                  </div>
                </Body.SelectUser>
              ))
            )}
          </Body.Scroll>
        </Body.HalfContent>
        
        {/* Detalhes do usuário selecionado */}
        {selectedUser && (
          <Body.HalfContent style={{ alignItems: 'center', width: '100px' }}>
            <Body.Photo
              src={selectedUser.fotoUsuario || Avatar}
              style={{ width: '120px', borderRadius: '60px', height: '120px' }}
            />
            
            <Body.Scroll style={{
              height: '100%',
              marginBottom: '10px',
              maxHeight: 'calc(100vh - 80px)',
              display: 'flex',
              flexDirection: 'column',
              gridGap: '20px 0px',
            }}>
              <Input
                text={userForm.nomeCompleto}
                setText={(value: string) => setUserForm(prev => ({ ...prev, nomeCompleto: value }))}
                title="Nome Completo"
                disabled={acessLevel === defines.CargoTectrol['Padrão']}
              />
              
              <Input
                text={userForm.email}
                setText={(value: string) => setUserForm(prev => ({ ...prev, email: value }))}
                title="Email"
                disabled={acessLevel === defines.CargoTectrol['Padrão']}
              />
              
              <Body.Row>
                <div style={{ width: '20%' }}>
                  <Input
                    text={userForm.ddd}
                    setText={(value: string) => setUserForm(prev => ({ ...prev, ddd: value }))}
                    title="DDD"
                    limit={2}
                    disabled={acessLevel === defines.CargoTectrol['Padrão']}
                  />
                </div>
                <div style={{ width: '79%' }}>
                  <Input
                    text={userForm.celular}
                    setText={(value: string) => setUserForm(prev => ({ ...prev, celular: value }))}
                    title="Telefone"
                    disabled={acessLevel === defines.CargoTectrol['Padrão']}
                  />
                </div>
              </Body.Row>
            </Body.Scroll>
            
            {acessLevel !== defines.CargoTectrol['Padrão'] && (
              <>
                <Body.Row>
                  <Button
                    text="Salvar"
                    action={updateUser}
                    width={155}
                    loading={loading}
                  />
                  <Button
                    text="Desvincular usuário"
                    action={() => setModalStates(prev => ({ ...prev, delete: true }))}
                    width={155}
                    color="yellow"
                    loading={loading}
                  />
                </Body.Row>
                <Body.Row>
                  <Button
                    text="Deletar usuário"
                    action={() => setModalStates(prev => ({ ...prev, hardDelete: true }))}
                    width={155}
                    color="red"
                    loading={loading}
                  />
                </Body.Row>
              </>
            )}
          </Body.HalfContent>
        )}
      </Body.Content>

      {/* Modais */}
      {modalStates.delete && selectedUser && (
        <Modals.ModalDeleteUser
          toggle={() => setModalStates(prev => ({ ...prev, delete: false }))}
          idUsuario={selectedUser.idUsuario}
          nome={selectedUser.nomeCompleto || ''}
          action={deleteUser}
          nomeFilial={nomeFilial}
        />
      )}
      
      {modalStates.hardDelete && selectedUser && (
        <HardDelete
          toggle={() => setModalStates(prev => ({ ...prev, hardDelete: false }))}
          idUsuario={selectedUser.idUsuario}
          nome={selectedUser.nomeCompleto || ''}
          action={hardDeleteUser}
          nomeFilial={nomeFilial}
        />
      )}
      
      {loading && <Loading />}
      
      {modalStates.createUser && (
        <SendInvite
          toggle={() => setModalStates(prev => ({ ...prev, createUser: false }))}
          idFilial={idFilial}
          nomeFilial={nomeFilial}
        />
      )}
    </Body.Container>
  );
};

export default ManageUsers;